import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useGetAlbumVideo } from '@vimeo/gctl-api/hooks/useAlbumVideo';
import { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useViewer } from '@vimeo/viewer';
import { useBaseUrl } from '@vimeo/viewer-showcase-ui/hooks/use-base-url';
import { DEFAULT_SHOWCASE_PLAYER_EMBED_OVERRIDE_QUERY } from '@vimeo/viewer-showcase-ui/utils/constants';
import { clipBadgeParser, getEventIdFromClip } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useAlbumStore } from '../state/albumStore';
import { VIDEOS_API_VERSION } from '../utils/constants';
import { contextUrlParser } from '../utils/helper';
import { useGetShowcase } from './use-get-showcase';

const DEFAULT_SHOWCASE_QUERY = {
  forceCollectionUri: true,
};

const SELECT_FIELDS: VideoFields[] = [
  'createdTime',
  'privacy',
  'status',
  'type',
  'page.comments',
  'descriptionHtml',
  'contentRating',
  'language',
  'link',
  'live', // TODO: get all live data for now, eventually we need less than this
  'name',
  'playerEmbedUrl',
  'embedPlayerConfigUrl', // This fields need albumSignature. TODO: This is used to load new videos in player temporarily, should be removed after we update @embed-player
  'user.link',
  'user.name',
  'user.pictures.sizes.link',
  'user.pictures.sizes.width',
  'user.uri',
  'user.backgroundVideo.aspectRatio',
  'user.backgroundVideo.clipId',
  'user.backgroundVideo.configUrl',
  'user.backgroundVideo.endTime',
  'user.backgroundVideo.firstFrameUrl',
  'user.backgroundVideo.startTime',
  'user.backgroundVideo.yPosition',
  'user.isStaffPicked',
  'user.pictures.sizes.link',
  'user.pictures.sizes.width',
  'user.locationDetails.formattedAddress',
  'user.skills.name',
  'user.metadata.connections.followers.total',
  'categories.name',
  'categories.link',
  'embed.badges.dolbyVision',
  'embed.badges.hdr_10',
  'embed.badges.hdr_10Plus',
  'metadata.aiContent',
  'metadata.interactions.interact',
  'metadata.connections.comments',
  'page',
  'metadata.interactions.like',
  'stats.plays',
];

export const useGetShowcaseVideo = (clipId: string | null) => {
  const viewer = useViewer();
  const router = useRouter();

  /**
   * Handle page url can have different pathname from resource links
   * e.g embedded showcase's pathname is `/showcase/11580063/embed2`
   * but the resource link is `/showcase/11580063`
   */
  const baseUrl = useBaseUrl(); // Use the custom hook for baseUrl

  const { albumId, albumSignature, password, isShowcaseEmbedded, referrer, continuousPlay } =
    useAlbumStore((state) => ({
      albumId: state.albumId,
      albumSignature: state.albumSignature,
      password: state.hashedPass,
      isShowcaseEmbedded: state.isShowcaseEmbedded,
      referrer: state.referrer,
      continuousPlay: state.continuousPlay,
    }));

  const embedQuery = {
    isEmbed: isShowcaseEmbedded,
    referrer,
  };

  /** Additional query params to send along API request */
  const { playerAccentColor, hideUpcoming } = useGetShowcase();

  const playerColorsQuery = {
    colors: ['000000e6', playerAccentColor || '00adef', 'ffffff', '000000'],
  };
  const playerAutoplayQuery = router.query.autoplay === '1' ? { autoplay: 1 } : {};

  const { data: clipData, ...gctlConfig } = useGetAlbumVideo(
    () => {
      if (!viewer || !clipId || albumSignature === null) return null;

      return {
        where: { albumId, videoId: Number(clipId) },
        select: SELECT_FIELDS,
        query: {
          ...DEFAULT_SHOWCASE_QUERY,
          ...DEFAULT_SHOWCASE_PLAYER_EMBED_OVERRIDE_QUERY,
          ...embedQuery,
          ...playerColorsQuery,
          ...playerAutoplayQuery,
          ...(password && { password }),
          ...(albumSignature && { albumSignature, albumUri: `albums/${albumId}` }),
          ...(hideUpcoming && { filter: 'playable' }),
          ...(continuousPlay && { outro: 'nothing' }),
        },
        headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      revalidateOnFocus: false,
      onError: (error) => {
        if (error.status === 404) {
          router.push(baseUrl || `/showcase/${albumId}`);
        }
      },
    },
  );

  // Persist the last non-null clipData
  const lastClipDataRef = useRef<typeof clipData>();
  useEffect(() => {
    if (clipData) {
      lastClipDataRef.current = clipData;
    }
  }, [clipData]);

  /**
   * Extract EventId for "archived" live clips
   *
   * GCTL GET Album hook return clipResponse, all clips, including Live Clips.
   *
   * Live clip is a essentially a clip, but it has an event "container" attached to it.
   * Here we gonna use the url with `?event=:eventId` to navigate to this event's live clip in the showcase.
   * Under the hood, player rendering still need to use the (live)clipId, so does video details data and other clip level data.
   *
   * Currently we don't need the event hash here, YET.
   */
  const eventId =
    (clipData && getEventIdFromClip(clipData)) ||
    (lastClipDataRef.current && getEventIdFromClip(lastClipDataRef.current));
  const clipSource = clipData || lastClipDataRef.current;
  const clip = clipSource
    ? {
        ...clipSource,
        ...contextUrlParser(clipSource.link, { baseUrl, eventId }),
        ...(eventId && { eventId }),
        ...clipBadgeParser(clipSource),
      }
    : null;

  return {
    clip,
    ...gctlConfig,
  };
};
